// ============================================================
//  src/api/client.js
//  Centralised Axios instance + all API call helpers
// ============================================================
import axios from 'axios';
import { navigateTo } from '../context/NavigationContext';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://cloverr.ng/api';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token from localStorage on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401 (invalid/expired token), clear auth and navigate to login
// Uses navigateTo() instead of window.location.href — no full page reload
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      navigateTo('/login');
    }
    return Promise.reject(error);
  }
);

// ── Account ──────────────────────────────────────────────────

export const login = (identifier, password) =>
  client.post('/account/login/', { identifier, password });

export const register = (data) =>
  client.post('/account/register/', data);

export const forgotPasswordRequest = (email) =>
  client.post('/account/forgot-password/', { step: 'request', email });

export const forgotPasswordVerify = (email, otp) =>
  client.post('/account/forgot-password/', { step: 'verify', email, otp });

export const forgotPasswordReset = (email, reset_token, password) =>
  client.post('/account/forgot-password/', { step: 'reset', email, reset_token, password });

// OTP-based email verification — POST { email, otp }
export const verifyEmail = (email, otp) =>
  client.post('/account/verify-email/', { email, otp });

export const resendVerification = (email) =>
  client.post('/account/resend-verification/', { email });

export const checkRef = (code) =>
  client.get(`/account/check-ref/?code=${code}`);

export const logout = () =>
  client.post('/account/logout/');

// ── Dashboard ─────────────────────────────────────────────────

export const getDashboard = () =>
  client.get('/user/dashboard/');

// ── Profile ───────────────────────────────────────────────────

export const getProfile = () =>
  client.get('/user/profile/');

export const updateProfile = (fname, lname) =>
  client.post('/user/profile/', { action: 'update_profile', fname, lname });

export const changePassword = (current_password, new_password, confirm_password) =>
  client.post('/user/profile/', { action: 'change_password', current_password, new_password, confirm_password });

export const updateAvatar = (formData) =>
  client.post('/user/avatar/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ── Wallet / Transactions ─────────────────────────────────────

export const getTransactions = (limit = 20, type = '') =>
  client.get('/user/transactions/', { params: { limit, type } });

export const getFundWalletInfo = () =>
  client.get('/user/fund-wallet/');

export const generateVirtualAccount = (bank) =>
  client.post('/user/fund-wallet/', { bank });

// ── Notifications ─────────────────────────────────────────────

export const getNotifications = () =>
  client.get('/user/notifications/');

// ── Tasks (Browse & Order) ────────────────────────────────────

export const getTasks = (provider_id = 0) =>
  client.get('/user/tasks/', { params: { provider_id } });

export const getTaskOrders = (status = '') =>
  client.get('/user/task-orders/', { params: { status } });

export const placeOrder = (service_id, quantity, target_url) =>
  client.post('/user/place-order/', { service_id, quantity, target_url });

// ── Content Services ──────────────────────────────────────────

export const getContentServices = () =>
  client.get('/user/content-services/');

export const getContentService = (id) =>
  client.get('/user/content-services/', { params: { id } });

export const placeContentOrder = (formData) =>
  client.post('/user/content-services/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// ── Earner ────────────────────────────────────────────────────

export const getEarnerDashboard = () =>
  client.get('/user/earner-dashboard/');

export const getEarnerTasks = () =>
  client.get('/user/earner-tasks/');

/**
 * Submit a task proof.
 * Accepts a pre-built FormData object from EarnerTaskDetailPage
 * so that file uploads (proof_file, ef_file_N) are sent correctly
 * as multipart/form-data.
 */
export const submitTask = (formData) =>
  client.post('/user/earner-tasks/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const getEarnerInfo = () =>
  client.get('/user/become-earner/');

export const becomeEarner = () =>
  client.post('/user/become-earner/');

export const getOrderProofs = (order_id) =>
  client.get('/user/order-proofs/', { params: { order_id } });

// ── Withdrawal ────────────────────────────────────────────────

export const getWithdrawInfo = () =>
  client.get('/user/withdraw/');

export const getBankList = () =>
  client.get('/user/withdraw/', { params: { banks: 1 } });

export const verifyBankAccount = (account_number, bank_code) =>
  client.get('/user/withdraw/', { params: { verify: 1, account_number, bank_code } });

export const submitWithdrawal = (data) =>
  client.post('/user/withdraw/', data);

// ── Referral ─────────────────────────────────────────────────

export const getReferral = () =>
  client.get('/user/referral/');

export const checkReferralCode = (code) =>
  client.post('/user/referral/', { action: 'check_code', code });

// ── PIN ───────────────────────────────────────────────────────

export const getPin = () =>
  client.get('/user/pin/');

export const setPin = (pin, current_pin) =>
  client.post('/user/pin/', { action: 'set_pin', pin, current_pin });

export const verifyPin = (pin) =>
  client.post('/user/pin/', { action: 'verify_pin', pin });

export const resetPin = () =>
  client.post('/user/pin/', { action: 'reset_pin' });

// ── Contact ───────────────────────────────────────────────────

export const getContact = () =>
  client.get('/user/contact/');

export default client;